import React from 'react';
import type { FC } from 'react';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { ButtonItem } from '@atlaskit/menu';

import type { ExpandableSubMenuItemProps } from './ExpandableSubMenuItem';

type PageAppsExpandableSubMenuItemProps = ExpandableSubMenuItemProps & {
	forgeMenuItems?: React.ReactElement[];
};

// this is to keep consistent with ContentTools PopupMenuGroup
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDropdownItemGroup = styled(DropdownItemGroup)({
	minWidth: '220px',
	maxWidth: '800px',
});

// styled(DropdownItem) doesn't apply css to component
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemLabelContainer = styled.span({
	display: 'flex',
	justifyContent: 'space-between',
});

const badgeContainerStyles = xcss({
	paddingLeft: 'space.075',
});

// Don't want to apply aui button styles in the dropdown menu since style needs to be uniform
export const getStyleClass = (styleClass?: string) => {
	return !styleClass?.includes('aui-button') ? styleClass : '';
};

export const PageAppsExpandableSubMenuItems: FC<PageAppsExpandableSubMenuItemProps> = ({
	label,
	items,
	isSubMenuOpen,
	onMouseEnter,
	onClick,
	forgeMenuItems,
}) => {
	return (
		<DropdownMenu
			placement="left-start"
			isOpen={isSubMenuOpen}
			trigger={({ triggerRef, ...triggerProps }) => (
				<ButtonItem
					{...triggerProps}
					ref={triggerRef}
					// @ts-ignore
					onMouseEnter={onMouseEnter}
					onClick={onClick}
					iconAfter={<ChevronRightIcon label="" />}
				>
					{label}
				</ButtonItem>
			)}
		>
			<StyledDropdownItemGroup>
				{items.map((item, index) => (
					// className is needed here for Connect modals to show properly
					<div
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={getStyleClass(item?.styleClass)}
						data-testid={`item-wrapper-${index}`}
						key={item.id}
					>
						<DropdownItem key={item.id} href={item.url} UNSAFE_shouldDisableRouterLink>
							<DropdownItemLabelContainer>
								{/* Some Connect apps don't have a label so we'll use the tooltip as the text since it's the next best thing */}
								<span>{item.label || item?.tooltip}</span>
								{item.badgeLabel ? (
									<Box xcss={badgeContainerStyles}>
										<Badge>{item.badgeLabel}</Badge>
									</Box>
								) : null}
							</DropdownItemLabelContainer>
						</DropdownItem>
					</div>
				))}
				{forgeMenuItems}
			</StyledDropdownItemGroup>
		</DropdownMenu>
	);
};
